import {
  Address,
  SolicitudCambioUserReq,
  User,
  UserAddressRes,
  UserPersonalRes,
  UserRes,
} from '../types/User/types';

// Mapeo de campos: original -> body
const USER_FIELD_MAP: Record<string, string> = {
  nombre: 'nombre',
  apellido: 'apellido',
  dni: 'dni',
  tel_celular: 'telCelular',
  fecha_nacimiento: 'fechaNac',
  genero: 'genero',
};

const ADDRESS_FIELD_MAP: Record<string, string> = {
  direccion: 'direccion',
  numero: 'numero',
  piso: 'piso',
  departamento: 'dpto',
  //   provincia: 'provincia',
  // Se compara solo localidad (ID único)
  localidad: 'localidad',
};

// Normalizar valor para comparación
const normalizeValue = (val: unknown): string => {
  if (val === null || val === undefined) return '';
  if (val instanceof Date) return val.toISOString().split('T')[0]; // "2025-12-17"
  return String(val).trim();
};

const hasValue = (value: unknown): boolean => normalizeValue(value).length > 0;
const hasValidBirthDate = (value: unknown): boolean => hasValue(value);

const getAddressLocalidadValue = (
  address?: UserAddressRes,
): number | string | null | undefined => {
  if (!address) return undefined;

  if (address.localidadId !== undefined && address.localidadId !== null) {
    return address.localidadId;
  }

  if (address.localidad_id !== undefined && address.localidad_id !== null) {
    return address.localidad_id;
  }

  const localidad = address.localidad;

  if (typeof localidad === 'object' && localidad !== null) {
    return localidad.id ?? localidad.value ?? localidad.nombre;
  }

  if (typeof localidad === 'number' || typeof localidad === 'string') {
    return localidad;
  }

  return undefined;
};

const getAddressDireccionValue = (address?: UserAddressRes): unknown =>
  address?.direccion ?? (address as any)?.calle;

const getAddressNumeroValue = (address?: UserAddressRes): unknown =>
  address?.numero ?? (address as any)?.altura;

const getUserPersonalData = (user: UserRes): UserPersonalRes =>
  user.datosPersona ?? user.datos_persona ?? user.persona ?? user;

const hasRootAddressData = (user: UserRes): boolean =>
  Boolean(
    user.direccion ||
      user.numero ||
      user.provincia ||
      user.localidadId ||
      user.localidad_id ||
      user.localidad,
  );

const getUserAddressData = (
  user: UserRes,
): UserAddressRes | null | undefined =>
  user.datosDomicilio ??
  user.datos_domicilio ??
  user.domicilio ??
  (hasRootAddressData(user) ? user : undefined);

const getUserBirthDate = (user: UserRes): unknown => {
  const personal = getUserPersonalData(user);
  return (
    personal.fecha_nacimiento ??
    personal.fechaNac ??
    personal.fecha_nac ??
    (personal as any).fechaNacimiento
  );
};

const getUserPhone = (user: UserRes): unknown => {
  const personal = getUserPersonalData(user);
  return personal.tel_celular ?? personal.telCelular;
};

export const isUserProfileComplete = (user?: UserRes | null): boolean => {
  if (!user) return false;
  const personal = getUserPersonalData(user);

  const hasPersonalBaseData = [
    personal.nombre,
    personal.apellido,
    personal.dni,
    personal.email,
    getUserPhone(user),
    personal.genero,
  ].every(hasValue);
  const hasPersonalData =
    hasPersonalBaseData && hasValidBirthDate(getUserBirthDate(user));

  const address = getUserAddressData(user);
  const hasAddressData = Boolean(
    address &&
      hasValue(address.direccion) &&
      hasValue(address.numero) &&
      hasValue(address.provincia) &&
      hasValue(getAddressLocalidadValue(address)),
  );

  return hasPersonalData && hasAddressData;
};

export const isDawerProfileCompletionComplete = (
  user?: UserRes | null,
): boolean => {
  if (!user) return false;
  const personal = getUserPersonalData(user);

  const hasRequiredPersonalData =
    hasValidBirthDate(getUserBirthDate(user)) && hasValue(personal.genero);

  const address = getUserAddressData(user);
  const hasRequiredLocalityData = Boolean(
    address && hasValue(getAddressLocalidadValue(address)),
  );

  return hasRequiredPersonalData && hasRequiredLocalityData;
};

export const canContractCoverage = (user?: UserRes | null): boolean => {
  return getCoverageContractMissingFields(user).length === 0;
};

export const getCoverageContractMissingFields = (
  user?: UserRes | null,
): string[] => {
  if (!user) return ['datos personales'];

  const personal = getUserPersonalData(user);
  const missingFields: string[] = [];

  if (!hasValidBirthDate(getUserBirthDate(user))) {
    missingFields.push('fecha de nacimiento');
  }

  if (!hasValue(personal.genero)) {
    missingFields.push('género');
  }

  const address = getUserAddressData(user);

  if (!address) {
    missingFields.push('domicilio');
    return missingFields;
  }

  if (!hasValue(getAddressDireccionValue(address))) {
    missingFields.push('dirección');
  }

  if (!hasValue(getAddressNumeroValue(address))) {
    missingFields.push('número de domicilio');
  }

  if (!hasValue(getAddressLocalidadValue(address))) {
    missingFields.push('localidad');
  }

  return missingFields;
};

// Comparar userData original con el body que se va a enviar
export const hasChangesInUserBody = (
  originalUser: User | undefined,
  originalAddress: Address | undefined,
  body: SolicitudCambioUserReq,
): boolean => {
  // Comparar campos de usuario
  for (const [originalKey, bodyKey] of Object.entries(USER_FIELD_MAP)) {
    const originalVal = normalizeValue(originalUser?.[originalKey as keyof User]);
    const bodyVal = normalizeValue(body[bodyKey as keyof SolicitudCambioUserReq]);

    if (originalVal !== bodyVal) {
      return true;
    }
  }

  // Comparar campos de dirección
  for (const [originalKey, bodyKey] of Object.entries(ADDRESS_FIELD_MAP)) {
    const originalVal = normalizeValue(originalAddress?.[originalKey as keyof Address]);
    const bodyVal = normalizeValue(body[bodyKey as keyof SolicitudCambioUserReq]);

    if (originalVal !== bodyVal) {
      return true;
    }
  }

  return false;
};
